import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createFAQStructuredData, createBreadcrumbStructuredData, createTableStructuredData } from '../../utils/structuredData';

export default function Spread() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXのスプレッドは本当に安い？他社と徹底比較 | 取引コスト検証',
    description: 'DMM FXのスプレッドを他社と徹底比較。ドル円0.2銭は本当に安いのか？主要通貨ペアのスプレッド比較表、実際の取引コスト例、原則固定スプレッドの特徴を詳しく解説。業界最狭水準の真実を検証します。',
    keywords: 'DMM FX,スプレッド,取引コスト,手数料,比較,ドル円,ユーロ円,業界最狭水準',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXのスプレッドは本当に安い？他社と徹底比較',
        description: 'DMM FXのスプレッドを他社と徹底比較。業界最狭水準と言われるスプレッドの真実を検証します。',
        author: 'DMM FX情報局',
        datePublished: '2024-11-05',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/60a9e9a457a2e9cd2e5ca84217d35132.jpg',
        url: '/spread',
      }),
      createFAQStructuredData([
        {
          question: 'スプレッドは常に固定ですか?',
          answer: '原則固定ですが、早朝(午前6時~7時)や経済指標発表時、市場の急変時などは拡大する場合があります。通常の取引時間帯では安定したスプレッドで取引できます。',
        },
        {
          question: 'スプレッド以外に手数料はかかりますか?',
          answer: 'DMM FXでは取引手数料、口座維持手数料、入出金手数料(クイック入金の場合)はすべて無料です。実質的なコストはスプレッドのみとなります。',
        },
        {
          question: '他社と比べてDMM FXのスプレッドは本当に安いですか?',
          answer: '主要通貨ペアでは業界最安水準を維持しています。特に米ドル/円、ユーロ/円などの人気通貨ペアでは他社と同等かそれ以上の安いスプレッドを提供しています。',
        },
        {
          question: 'スプレッドが広がりやすい時間帯はいつですか?',
          answer: '早朝(午前6時~7時)、週明けの窓開け時、重要な経済指標発表時、年末年始などの流動性が低下する時間帯はスプレッドが広がりやすくなります。',
        },
      ]),
      createTableStructuredData({
        about: 'DMM FX、GMOクリック証券、楽天証券の主要通貨ペアスプレッド比較表',
        description: '米ドル/円、ユーロ/円、ポンド/円、豪ドル/円、ユーロ/米ドル、ポンド/米ドルのスプレッドを3社で比較',
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'スプレッド比較', url: '/spread' },
      ]),
    ],
  });

  const spreadData = [
    { pair: '米ドル/円', dmm: '0.2銭', gmo: '0.2銭', rakuten: '0.2銭' },
    { pair: 'ユーロ/円', dmm: '0.4銭', gmo: '0.4銭', rakuten: '0.5銭' },
    { pair: 'ポンド/円', dmm: '0.9銭', gmo: '0.9銭', rakuten: '1.0銭' },
    { pair: '豪ドル/円', dmm: '0.5銭', gmo: '0.5銭', rakuten: '0.7銭' },
    { pair: 'ユーロ/米ドル', dmm: '0.3pips', gmo: '0.3pips', rakuten: '0.4pips' },
    { pair: 'ポンド/米ドル', dmm: '0.9pips', gmo: '1.0pips', rakuten: '1.0pips' },
  ];

  const features = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: '業界最安水準のスプレッド',
      description:
        '主要通貨ペアで業界最安水準のスプレッドを提供。取引コストを最小限に抑えられます。',
    },
    {
      icon: 'ri-time-line',
      title: '原則固定スプレッド',
      description:
        '例外時間帯を除き、スプレッドは原則固定。安定した取引環境を実現しています。',
    },
    {
      icon: 'ri-line-chart-line',
      title: '透明性の高い提示',
      description:
        'スプレッドの提示率が高く、約定力も優秀。スリッページのリスクが低いです。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '取引コストが明確',
      description:
        '取引手数料は無料。スプレッドのみが実質的なコストなので計算しやすいです。',
    },
  ];

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
            { name: 'スプレッド比較', url: '/spread' }
          ]} />
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-20 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              DMM FXのスプレッドは<br />
              本当に安い?
            </h1>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto mb-6 md:mb-10 px-2">
              <Highlight variant="marker" color="red">業界最安水準</Highlight>のスプレッドを誇るDMM FX。主要通貨ペアのスプレッドを他社と徹底比較し、実際の取引コストを検証します。
            </p>
          </div>

          {/* スプレッドの説明 */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-5 md:p-8 lg:p-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-5 flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <i className="ri-exchange-dollar-line text-base md:text-xl text-white"></i>
                </div>
                そもそもスプレッドとは?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4 md:mb-6">
                スプレッドとは、FX取引における<strong>「買値（Ask）」と「売値（Bid）」の差額</strong>
                のことです。この差額がFX会社の実質的な手数料にあたり、トレーダーにとっての取引コストとなります。
              </p>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
                <h3 className="font-bold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">具体例で理解する</h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-2 md:mb-3">
                  例えば、米ドル/円のスプレッドが<Highlight variant="bold" color="red">0.2銭</Highlight>の場合：
                </p>
                <ul className="space-y-1 md:space-y-2 text-gray-700 text-xs md:text-base">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                    <span>買値が150.003円、売値が150.001円 → 差額0.002円（<Highlight variant="bold" color="red">0.2銭</Highlight>）</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
                    <span>
                      1万通貨の取引なら、1回あたりのコストはわずか<Highlight variant="bold" color="red">20円</Highlight>
                    </span>
                  </li>
                </ul>
              </div>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                スプレッドが安い（狭い）ほど取引コストが低くなるため、特に取引回数が多いトレーダーにとっては非常に重要なポイントです。DMM
                FXは主要通貨ペアで<Highlight variant="marker" color="red">業界最安水準</Highlight>のスプレッドを提供しており、コストを抑えた効率的な取引が可能です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spread Comparison Table */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            主要通貨ペアのスプレッド比較
          </h2>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6 md:mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm md:text-base">
                <thead className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
                  <tr>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold">通貨ペア</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-semibold">DMM FX</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-semibold">GMO</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-semibold">楽天</th>
                  </tr>
                </thead>
                <tbody>
                  {spreadData.map((item, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium text-gray-900">
                        {item.pair}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-center">
                        <span className="inline-block bg-teal-100 text-teal-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-semibold">
                          <Highlight variant="bold" color="red">{item.dmm}</Highlight>
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm text-gray-600">
                        {item.gmo}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm text-gray-600">
                        {item.rakuten}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-500 p-4 md:p-6 rounded-lg">
            <div className="flex items-start gap-2 md:gap-3">
              <i className="ri-information-line text-xl md:text-2xl text-teal-600 mt-0.5 md:mt-1"></i>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">スプレッドについて</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                  スプレッドは原則固定ですが、市場の急変時や流動性が低下する時間帯(早朝など)は拡大する場合があります。また、経済指標発表時なども一時的に広がることがあります。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">
            DMM FXのスプレッドの特徴
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 md:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-5 md:p-8 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <i className={`${feature.icon} text-xl md:text-2xl text-white`}></i>
                </div>
                <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                  {index === 0 ? <Highlight variant="underline" color="red">{feature.title}</Highlight> : feature.title}
                </h3>
                <p className="text-xs md:text-base text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-8 md:mt-12 bg-white rounded-xl p-4 md:p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Cost Calculation Example */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            実際の取引コスト例
          </h2>

          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-5 md:p-8 mb-6 md:mb-8">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">
              米ドル/円を1万通貨取引した場合
            </h3>

            <div className="space-y-3 md:space-y-4">
              <div className="bg-white rounded-lg p-4 md:p-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm md:text-base text-gray-700 font-medium">スプレッド</span>
                  <span className="text-xl md:text-2xl font-bold text-teal-600">
                    <Highlight variant="bold" color="red">0.2銭</Highlight>
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm md:text-base text-gray-700 font-medium">取引コスト(1万通貨)</span>
                  <span className="text-xl md:text-2xl font-bold text-gray-900">
                    <Highlight variant="bold" color="red">20円</Highlight>
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 md:p-6">
                <h4 className="font-semibold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">
                  月間100回取引した場合の年間コスト
                </h4>
                <div className="text-center">
                  <p className="text-xs md:text-sm text-gray-600 mb-2">
                    20円 × 100回 × 12ヶ月 =
                  </p>
                  <p className="text-2xl md:text-3xl font-bold text-teal-600">
                    <Highlight variant="bold" color="red">24,000円</Highlight>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 md:p-6 rounded-lg">
            <div className="flex items-start gap-2 md:gap-3">
              <i className="ri-lightbulb-line text-xl md:text-2xl text-yellow-600 mt-0.5 md:mt-1"></i>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">
                  スプレッドが安いメリット
                </h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                  スプレッドが安いほど取引コストが低くなり、特に短期売買やスキャルピングを行うトレーダーにとって大きなメリットとなります。年間で見ると数万円の差になることも。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12 text-center">
            よくある質問
          </h2>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-xl p-5 md:p-8 shadow-md">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                スプレッドは常に固定ですか?
              </h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed pl-6 md:pl-8">
                原則固定ですが、早朝(午前6時~7時)や経済指標発表時、市場の急変時などは拡大する場合があります。通常の取引時間帯では安定したスプレッドで取引できます。
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-8 shadow-md">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                スプレッド以外に手数料はかかりますか?
              </h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed pl-6 md:pl-8">
                DMM FXでは取引手数料、口座維持手数料、入出金手数料(クイック入金の場合)はすべて無料です。実質的なコストはスプレッドのみとなります。
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-8 shadow-md">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                他社と比べてDMM FXのスプレッドは本当に安いですか?
              </h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed pl-6 md:pl-8">
                主要通貨ペアでは業界最安水準を維持しています。特に米ドル/円、ユーロ/円などの人気通貨ペアでは他社と同等かそれ以上の安いスプレッドを提供しています。
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 md:p-8 shadow-md">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3 flex items-center gap-2">
                <i className="ri-question-line text-teal-600"></i>
                スプレッドが広がりやすい時間帯はいつですか?
              </h3>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed pl-6 md:pl-8">
                早朝(午前6時~7時)、週明けの窓開け時、重要な経済指標発表時、年末年始などの流動性が低下する時間帯はスプレッドが広がりやすくなります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            スプレッドについてさらに詳しく知りたい方へ
          </h2>
          
          <div className="grid grid-cols-2 gap-3 md:gap-6">
            <Link 
              to="/comparison" 
              className="group bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-4 md:p-6 border-2 border-teal-100 hover:border-teal-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex flex-col md:flex-row items-start gap-2 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-bar-chart-box-line text-lg md:text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors text-sm md:text-base">
                    他社との徹底比較
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
                    DMM FXと他のFX会社のスプレッドやサービスを詳しく比較
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/trading-hours" 
              className="group bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 md:p-6 border-2 border-emerald-100 hover:border-emerald-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex flex-col md:flex-row items-start gap-2 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-time-line text-lg md:text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-emerald-600 transition-colors text-sm md:text-base">
                    取引時間とスプレッド
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
                    時間帯によるスプレッドの変動と最適な取引タイミング
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/disadvantages" 
              className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 md:p-6 border-2 border-amber-100 hover:border-amber-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex flex-col md:flex-row items-start gap-2 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-alert-line text-lg md:text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-amber-600 transition-colors text-sm md:text-base">
                    DMM FXのデメリット
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
                    スプレッド以外の注意点やデメリットも確認しておこう
                  </p>
                </div>
              </div>
            </Link>

            <Link 
              to="/deposit-withdrawal" 
              className="group bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl p-4 md:p-6 border-2 border-cyan-100 hover:border-cyan-300 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex flex-col md:flex-row items-start gap-2 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-exchange-dollar-line text-lg md:text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-cyan-600 transition-colors text-sm md:text-base">
                    入出金方法と手数料
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
                    スプレッド以外のコスト、入出金手数料について詳しく解説
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Important Message Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-6 md:p-8 lg:p-12 text-center shadow-xl">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              <i className="ri-check-double-line text-2xl md:text-3xl text-white"></i>
            </div>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-4 md:mb-6">
              スプレッドはFX会社選びで<br className="md:hidden" />とても重要なポイント
            </h3>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 mb-4 md:mb-6">
              <p className="text-sm md:text-base lg:text-lg text-white leading-relaxed">
                DMM FXは<strong className="text-yellow-300"><Highlight variant="bold" color="red">業界最安水準</Highlight></strong>でコストを抑えられます。<br className="hidden md:block" />
                これから始める初心者にとって、無駄なコストがかからないのは大きなメリットです。<br className="hidden md:block" />
                まずは公式サイトで詳細を確認してみてください。
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs md:text-sm text-white/80">
              <div className="flex items-center gap-1">
                <i className="ri-shield-check-line"></i>
                <span>口座開設・維持費無料</span>
              </div>
              <span className="hidden sm:inline mx-2">|</span>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>最短即日で取引開始</span>
              </div>
            </div>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mt-6 md:mt-8 bg-white rounded-xl p-4 md:p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-teal-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            業界最安水準のスプレッドで<br />
            取引コストを抑えよう
          </h2>
          <p className="text-sm md:text-lg text-teal-50 mb-6 md:mb-8 max-w-2xl mx-auto">
            DMM FXなら主要通貨ペアで業界最安水準のスプレッド。今すぐ口座開設して、低コストな取引を始めましょう。
          </p>
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-bold mb-4 animate-pulse">
            <i className="ri-gift-line"></i>
            <span>👉 今だけ最大50万円キャッシュバック</span>
          </div>
          <div className="block">
            <a
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82"
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-6 md:px-8 py-4 md:py-5 rounded-xl text-base md:text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
            >
              👉 今だけ最大50万円キャッシュバック<br />
              無料でDMM FX口座を開設する
            </a>
            <img
              border="0"
              width="1"
              height="1"
              src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82"
              alt=""
              style={{ display: 'none' }}
            />
          </div>
          <p className="text-xs md:text-sm text-teal-50 mt-4 md:mt-6">
            最短即日で取引開始可能 | 口座開設・維持費無料
          </p>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
